import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * 화면을 바꿀 때 스크롤 위치 (12회차 M28) — 규칙 셋.
 *
 *  ① 경로(pathname)가 바뀌는 앞으로 가기(PUSH · REPLACE — 링크 · 카드 · PREV/NEXT · 「비슷한 원단」 · 로그아웃 뒤 홈)는 맨 위에서 연다.
 *     예전에는 앞 화면의 스크롤 위치에서 열려 상세의 사진 · 이름 · 3탭이 화면 위로 지나가 있었다(BrowserRouter 는 스크롤을 다루지 않는다).
 *  ② 뒤로 · 앞으로(POP)는 그 기록을 떠날 때의 위치로 되돌린다. 브라우저 기본 복원은 목록이 서버에서 오기 전(골격 높이)에 돌아
 *     1280 전체 원단 867 → 뒤로 354 처럼 모자라게 멈췄다 — 그래서 위치를 기록마다(location.key) 적어 두고, 화면이 그만큼 길어지면
 *     그때 한 번 옮긴다(최대 3초, 그사이 사용자가 휠 · 터치 · 키 · 누르기를 하면 그만둔다). 새로고침 · 다른 사이트에서 뒤로 온 첫 화면도 같다.
 *  ③ 같은 경로에서 질의만 바뀌는 이동(필터 · 정렬 · 쪽 넘김 · 탭)은 건드리지 않는다 — 그 화면이 정한다
 *     (검색 결과 쪽 넘김은 맨 위, 전체 원단 쪽 넘김은 목록 머리, 필터는 그 자리).
 *
 * layout effect — 새 화면을 그린 그 커밋 안에서 칠하기 전에 옮긴다(앞 위치가 한 번 보였다가 튀지 않게).
 * 화면이 자기 첫 그리기 뒤(passive effect)에 스스로 굴리는 것(내 정보 ?pw=1 → 비밀번호 칸)은 이 뒤에 돌아 그대로 이긴다.
 */
const STORE = 'aitex.scroll';
const KEEP = 60;

function readStore(): Record<string, number> {
  try { const v = JSON.parse(sessionStorage.getItem(STORE) ?? '{}'); return v && typeof v === 'object' ? v : {}; } catch { return {}; }
}
let mem: Record<string, number> = typeof window === 'undefined' ? {} : readStore();
function persist(): void {
  const ks = Object.keys(mem);
  if (ks.length > KEEP) for (const k of ks.slice(0, ks.length - KEEP)) delete mem[k];
  try { sessionStorage.setItem(STORE, JSON.stringify(mem)); } catch { /* 저장소가 막혀 있으면 이번 탭 메모리만 */ }
}

/** 이 문서를 연 방식 — 새로고침 · 뒤로/앞으로로 연 첫 화면만 적어 둔 위치로 되돌린다(주소를 새로 친 첫 화면은 맨 위) */
function firstLoadRestores(): boolean {
  const nav = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
  return nav?.type === 'reload' || nav?.type === 'back_forward';
}

let restoreGen = 0;
/** 화면이 y 만큼 길어지면 그때 옮긴다 — 사용자가 먼저 움직이거나 다른 이동이 오면 그만둔다 */
function restoreTo(y: number): void {
  const my = ++restoreGen;
  const t0 = performance.now();
  let stopped = false;
  const evs = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
  const stop = () => { stopped = true; };
  evs.forEach((e) => window.addEventListener(e, stop, { capture: true, passive: true }));
  const done = () => evs.forEach((e) => window.removeEventListener(e, stop, { capture: true }));
  const tick = () => {
    if (stopped || my !== restoreGen) return done();
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max >= y - 1 || performance.now() - t0 > 3000) {
      window.scrollTo(0, Math.min(y, Math.max(0, max)));
      return done();
    }
    // setTimeout — 숨은 탭에서는 그리기 틀(rAF)이 돌지 않아 복원이 멈췄다(timer 는 느리게라도 돈다)
    window.setTimeout(tick, 16);
  };
  tick();
}

export function useRouteScroll(): void {
  const loc = useLocation();
  const navType = useNavigationType();
  const id = `${loc.key}|${loc.pathname}${loc.search}`;
  const cur = useRef(id);
  const lastPath = useRef<string | null>(null);

  // 지금 기록의 위치를 적는다 — 스크롤할 때마다, 그리고 화면을 바꾸기 **전**(누르기 · 키 · 클릭 · 뒤로가기 popstate 를 잡는 단계).
  // 숨은 탭은 스크롤 이벤트가 그리기 틀에서만 나가 오지 않으므로 떠나기 직전 값이 꼭 필요하다
  useEffect(() => {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const on = () => { mem[cur.current] = Math.round(window.scrollY); };
    const hide = () => { on(); persist(); };
    const opt = { capture: true, passive: true } as const;
    window.addEventListener('scroll', on, { passive: true });
    (['pointerdown', 'keydown', 'click', 'popstate'] as const).forEach((e) => window.addEventListener(e, on, opt));
    window.addEventListener('pagehide', hide);
    return () => {
      window.removeEventListener('scroll', on);
      (['pointerdown', 'keydown', 'click', 'popstate'] as const).forEach((e) => window.removeEventListener(e, on, opt));
      window.removeEventListener('pagehide', hide);
    };
  }, []);

  useLayoutEffect(() => {
    const first = lastPath.current === null;
    const samePath = lastPath.current === loc.pathname;
    lastPath.current = loc.pathname;
    cur.current = id;
    persist();
    // 13회차 N23: 어떤 이동이든 먼저 기다리던 복원을 그만둔다 — 저장 위치 없는 POP 이 맨 위로 간 뒤 앞 화면의 복원 타이머가
    // 새 화면을 다시 굴렸다(0 → 200). 새 복원이 필요하면 restoreTo 가 새 번호로 시작한다
    restoreGen += 1;
    if (navType === 'POP') {
      const y = mem[id];
      if (y != null && (!first || firstLoadRestores())) restoreTo(y);
      else if (!first && !samePath) window.scrollTo(0, 0);   // 적어 둔 위치가 없는 다른 화면 — 앞 화면 위치에 두지 않는다
      return;
    }
    if (!first && !samePath) window.scrollTo(0, 0);
    else mem[id] = Math.round(window.scrollY);   // 같은 화면의 바꿔 넣기(상세의 목록 위치 갱신 등) — 지금 위치를 새 기록으로 옮긴다
  }, [id]);   // eslint-disable-line react-hooks/exhaustive-deps
}
